/**
 * Sidebar Cmd/Ctrl+K keyboard-shortcut hook. Extracted verbatim
 * (behavior-preserving) from the `handleSidebarSearchShortcut` keydown
 * `useEffect` in layout/Sidebar.tsx.
 *
 * The hook occupies the exact slot where the original effect sat, so hook order
 * in the component body is unchanged. The dependency array is byte-identical to
 * the original (`[isCollapsed, isMobile]`); `setIsCollapsed` and
 * `searchInputRef` are stable identities threaded in as arguments.
 */

import { useEffect } from 'react';
import type { RefObject } from 'react';

interface UseSidebarKeyboardShortcutsArgs {
  isMobile: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
}

export function useSidebarKeyboardShortcuts({
  isMobile,
  isCollapsed,
  setIsCollapsed,
  searchInputRef,
}: UseSidebarKeyboardShortcutsArgs): void {
  useEffect(() => {
    if (isMobile) return;

    const handleSidebarSearchShortcut = (event: KeyboardEvent) => {
      const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!isShortcut) return;

      event.preventDefault();
      if (isCollapsed) {
        setIsCollapsed(false);
      }
      // Focus the search input
      setTimeout(() => searchInputRef.current?.focus(), 100);
    };

    window.addEventListener('keydown', handleSidebarSearchShortcut);
    return () => window.removeEventListener('keydown', handleSidebarSearchShortcut);
  }, [isCollapsed, isMobile]);
}
